import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import NeuralField from "./NeuralField";
import { Container, Eyebrow, Display, Lead, useReveal } from "./ui";
import { colors, fonts, media } from "../../styles/theme";
import { demo } from "../../pages/landingContent";

// "Feel the speed": a passage about vimbal shown one word at a time, up to 600 wpm.

const MIN_WPM = 100;
const MAX_WPM = 600;

const DemoWrap = styled.section`
  padding: 140px 0;
`;

const DemoGrid = styled.div`
  display: grid;
  gap: 40px;

  @media ${media.large} {
    grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.2fr);
    gap: 64px;
    align-items: center;
  }
`;

const DemoNote = styled.p`
  margin-top: 20px;
  font-family: ${fonts.body};
  font-size: 14px;
  line-height: 1.5;
  color: ${colors.pebble};
  max-width: 460px;
`;

const Reader = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 32px;
  background: ${colors.forest};
  color: ${colors.linen};
  box-shadow: 0 50px 100px -50px rgba(30, 42, 34, 0.7);
`;

const Screen = styled.div`
  position: relative;
  height: 300px;
  display: grid;
  place-items: center;
  cursor: pointer;
  outline: none;

  @media ${media.medium} {
    height: 340px;
  }

  canvas {
    opacity: ${(p) => (p.$playing ? 0.45 : 0.25)};
    transition: opacity 0.6s ease;
  }
  &:focus-visible {
    box-shadow: inset 0 0 0 3px ${colors.copperLight};
  }
`;

const Guide = styled.div`
  position: absolute;
  left: 50%;
  width: 2px;
  height: 18px;
  background: rgba(250, 247, 241, 0.35);
  transform: translateX(-50%);
  top: ${(p) => (p.$top ? "calc(50% - 58px)" : "calc(50% + 40px)")};
`;

const Word = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  font-family: ${fonts.body};
  font-weight: 500;
  font-size: clamp(40px, 6vw, 64px);
  letter-spacing: 0.01em;
  line-height: 1;
  white-space: pre;

  span:first-child {
    text-align: right;
  }
  b {
    font-weight: 600;
    color: ${colors.copperLight};
  }
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  display: grid;
  place-items: center;
  align-content: center;
  gap: 14px;
  padding: 24px;
  text-align: center;
  background: rgba(30, 42, 34, ${(p) => (p.$solid ? 0.94 : 0.6)});
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);

  strong {
    font-family: ${fonts.display};
    font-weight: 300;
    font-size: clamp(26px, 3vw, 38px);
    letter-spacing: -0.02em;
  }
  small {
    font-family: ${fonts.body};
    font-size: 16px;
    color: rgba(250, 247, 241, 0.8);
  }
`;

const BigPlay = styled.i`
  display: grid;
  place-items: center;
  width: 76px;
  height: 76px;
  border-radius: 50%;
  font-style: normal;
  font-size: 26px;
  color: ${colors.linen};
  background: linear-gradient(135deg, ${colors.copperLight}, ${colors.copper});
  box-shadow: 0 16px 40px -14px rgba(143, 83, 41, 0.9);
`;

const Progress = styled.div`
  height: 4px;
  background: rgba(250, 247, 241, 0.1);

  i {
    display: block;
    height: 100%;
    background: linear-gradient(90deg, ${colors.sage}, ${colors.copperLight});
    transition: width 0.15s linear;
  }
`;

const Controls = styled.div`
  display: grid;
  gap: 18px;
  padding: 22px;
  background: rgba(250, 247, 241, 0.04);

  @media ${media.medium} {
    padding: 26px 30px 30px;
  }
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 14px;
`;

const Btn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 46px;
  min-width: 46px;
  padding: 0 18px;
  border-radius: 999px;
  cursor: pointer;
  font-family: ${fonts.body};
  font-size: 15px;
  font-weight: 600;
  color: ${(p) => (p.$primary ? colors.forest : colors.linen)};
  background: ${(p) => (p.$primary ? colors.copperLight : "rgba(250, 247, 241, 0.08)")};
  border: 1px solid ${(p) => (p.$primary ? colors.copperLight : "rgba(250, 247, 241, 0.2)")};
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-1px);
  }
`;

const Speed = styled.div`
  flex: 1;
  min-width: 220px;
  display: grid;
  gap: 6px;

  label {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    font-family: ${fonts.mono};
    font-size: 11px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: rgba(250, 247, 241, 0.65);
  }
  output {
    font-family: ${fonts.display};
    font-size: 30px;
    letter-spacing: -0.03em;
    text-transform: none;
    color: ${colors.linen};
  }
  output small {
    font-family: ${fonts.mono};
    font-size: 11px;
    margin-left: 4px;
    color: rgba(250, 247, 241, 0.6);
  }
  input {
    width: 100%;
    accent-color: ${colors.copperLight};
    -webkit-appearance: auto;
    appearance: auto;
    cursor: pointer;
  }
`;

const Presets = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Preset = styled.button`
  display: grid;
  text-align: left;
  gap: 2px;
  padding: 8px 14px;
  border-radius: 14px;
  cursor: pointer;
  border: 1px solid ${(p) => (p.$on ? colors.copperLight : "rgba(250, 247, 241, 0.18)")};
  background: ${(p) => (p.$on ? "rgba(209, 154, 102, 0.18)" : "transparent")};
  color: ${colors.linen};
  transition: border-color 0.2s ease, background 0.2s ease;

  b {
    font-family: ${fonts.display};
    font-weight: 400;
    font-size: 18px;
  }
  span {
    font-family: ${fonts.mono};
    font-size: 10px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: rgba(250, 247, 241, 0.6);
  }
  &:hover {
    border-color: ${colors.copperLight};
  }
`;

const Stats = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 8px 12px;
  font-family: ${fonts.mono};
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(250, 247, 241, 0.6);
`;

// The letter the eye fixates on, so each word stays anchored in the same spot.
const focusIndex = (w) => {
  const n = w.replace(/[^A-Za-z0-9']/g, "").length;
  if (n <= 1) return 0;
  if (n <= 5) return 1;
  if (n <= 9) return 2;
  if (n <= 13) return 3;
  return 4;
};

// Linger a little on punctuation and long words, like a natural reading rhythm.
const delayFor = (w, wpm) => {
  const base = 60000 / wpm;
  if (/[.!?]$/.test(w)) return base * 2.2;
  if (/[,;:]$/.test(w)) return base * 1.6;
  if (w.length > 9) return base * 1.25;
  return base;
};

const fmtTime = (ms) => {
  const s = Math.max(0, Math.round(ms / 1000));
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, "0")}s`;
};

const WORDS = demo.passage.split(/\s+/);
const LAST = WORDS.length - 1;

const SpeedReader = () => {
  const scope = useRef(null);
  const screenRef = useRef(null);
  const startedAt = useRef(0);
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [wpm, setWpm] = useState(250);
  const [elapsed, setElapsed] = useState(0);
  useReveal(scope);

  const finished = index >= LAST && !playing && elapsed > 0;

  // Advance to the next word; stop at the end.
  useEffect(() => {
    if (!playing) return undefined;
    if (index >= LAST) {
      const id = setTimeout(() => {
        setElapsed((e) => e + (performance.now() - startedAt.current));
        setPlaying(false);
      }, delayFor(WORDS[index], wpm));
      return () => clearTimeout(id);
    }
    const id = setTimeout(() => setIndex((i) => i + 1), delayFor(WORDS[index], wpm));
    return () => clearTimeout(id);
  }, [playing, index, wpm]);

  // Pause when the reader scrolls out of view.
  useEffect(() => {
    const el = screenRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) {
        setPlaying((was) => {
          if (was) setElapsed((t) => t + (performance.now() - startedAt.current));
          return false;
        });
      }
    });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const play = () => {
    startedAt.current = performance.now();
    setPlaying(true);
  };

  const toggle = () => {
    if (playing) {
      setElapsed((e) => e + (performance.now() - startedAt.current));
      setPlaying(false);
      return;
    }
    if (index >= LAST) {
      setIndex(0);
      setElapsed(0);
    }
    play();
  };

  const restart = () => {
    setIndex(0);
    setElapsed(0);
    play();
  };

  const onKey = (e) => {
    if (e.key === " " || e.key === "Enter") {
      e.preventDefault();
      toggle();
    }
  };

  const word = WORDS[index] || "";
  const f = Math.min(focusIndex(word), Math.max(0, word.length - 1));
  const remainingMs = WORDS.slice(index + 1).reduce((t, w) => t + delayFor(w, wpm), 0);
  const effectiveWpm = finished ? Math.round(WORDS.length / (elapsed / 60000)) : null;

  return (
    <DemoWrap id="demo" ref={scope} aria-labelledby="demo-title">
      <Container>
        <DemoGrid>
          <div data-reveal>
            <Eyebrow $color={colors.copper}>{demo.eyebrow}</Eyebrow>
            <Display id="demo-title" style={{ marginTop: 20 }}>
              {demo.titleLead} <em>{demo.titleEmphasis}</em>
            </Display>
            <Lead style={{ marginTop: 24 }}>{demo.body}</Lead>
            <DemoNote>{demo.note}</DemoNote>
          </div>

          <Reader data-reveal="0.1">
            <Screen
              ref={screenRef}
              $playing={playing}
              role="button"
              tabIndex={0}
              aria-label={playing ? "Pause reading" : "Start reading"}
              onClick={toggle}
              onKeyDown={onKey}
            >
              <NeuralField variant="dark" calm={playing ? 1 : 0.5} lines={7} />
              <Guide $top aria-hidden="true" />
              <Guide aria-hidden="true" />
              <Word aria-hidden={!playing}>
                <span>{word.slice(0, f)}</span>
                <b>{word.charAt(f)}</b>
                <span>{word.slice(f + 1)}</span>
              </Word>
              {!playing && (
                <Overlay $solid={finished}>
                  <BigPlay aria-hidden="true">{finished ? "↻" : "▶"}</BigPlay>
                  {finished ? (
                    <>
                      <strong>
                        {WORDS.length} words in {fmtTime(elapsed)}
                      </strong>
                      <small>
                        That's about {effectiveWpm} words per minute. Try again, a little faster.
                      </small>
                    </>
                  ) : (
                    <>
                      <strong>{index === 0 ? "Press play to start reading" : "Paused"}</strong>
                      <small>Keep your eyes on the copper letter.</small>
                    </>
                  )}
                </Overlay>
              )}
            </Screen>

            <Progress aria-hidden="true">
              <i style={{ width: `${(index / LAST) * 100}%` }} />
            </Progress>

            <Controls>
              <Row>
                <Btn type="button" $primary onClick={toggle}>
                  {playing ? "❚❚ Pause" : finished ? "↻ Read again" : "▶ Play"}
                </Btn>
                <Btn type="button" onClick={restart} aria-label="Restart from the beginning">
                  ↻
                </Btn>
                <Speed>
                  <label htmlFor="wpm">
                    Speed
                    <output htmlFor="wpm">
                      {wpm}
                      <small>wpm</small>
                    </output>
                  </label>
                  <input
                    id="wpm"
                    type="range"
                    min={MIN_WPM}
                    max={MAX_WPM}
                    step={25}
                    value={wpm}
                    onChange={(e) => setWpm(Number(e.target.value))}
                    aria-valuetext={`${wpm} words per minute`}
                  />
                </Speed>
              </Row>
              <Presets role="group" aria-label="Speed presets">
                {demo.presets.map((p) => (
                  <Preset key={p.wpm} type="button" $on={p.wpm === wpm} aria-pressed={p.wpm === wpm} onClick={() => setWpm(p.wpm)}>
                    <b>{p.wpm}</b>
                    <span>{p.label}</span>
                  </Preset>
                ))}
              </Presets>
              <Stats>
                <span>
                  Word {index + 1} of {WORDS.length}
                </span>
                <span>{fmtTime(remainingMs)} left at this speed</span>
              </Stats>
            </Controls>
          </Reader>
        </DemoGrid>
      </Container>
    </DemoWrap>
  );
};

export default SpeedReader;
